import {
  MediaManagerMultiUploadBatchEvent,
  MediaManagerMultiUploadBatchStatus,
  MediaManagerMultiUploadEvent,
  MediaManagerMultiUploadItemEvent,
  MediaManagerMultiUploadItemState,
  MediaManagerMultiUploadState
} from '../../../models/media-manager/media-manager-multi-upload-state';
import { BudsenseFile } from '../../../models/shared/budsense-file';
import { MediaType, MediaTypeGroup } from '../../../models/enum/dto/media-type.enum';
import { MediaUtils } from '../../../utils/media-utils';
import { environment } from '../../../../environments/environment';
import { AllowedUploadTypesConfig, UploadUtils } from 'src/app/utils/upload-utils';

/**
 * Pure state machine for a multi-file upload batch. The domain model feeds it events (via a `scan` on
 * `_multiUploadEvents`) and gets back a new {@link MediaManagerMultiUploadState}; no I/O, no injectables, no
 * side effects. All the reducer branches return either a new object or the same reference when nothing changed.
 *
 * Item events roll into the batch state. Batch events (start, clear, view refresh) drive high-level status.
 */
export function reduceMultiUploadState(
  batchState: MediaManagerMultiUploadState,
  event: MediaManagerMultiUploadEvent
): MediaManagerMultiUploadState {
  if (event.scope === 'item') {
    const state = reduceMultiUploadItemEventInBatch(batchState, event);
    return deriveMultiUploadBatchStateFromItems(state);
  }
  return reduceMultiUploadBatchState(batchState, event);
}

/** Route an item event to the matching item by file identity, then re-summarize the batch counters. */
export function reduceMultiUploadItemEventInBatch(
  batchState: MediaManagerMultiUploadState,
  itemEvent: MediaManagerMultiUploadItemEvent
): MediaManagerMultiUploadState {
  const itemStates = batchState.itemStates.map(item => {
    if (item.file !== itemEvent.file) {
      return item;
    }
    return reduceMultiUploadItemState(item, itemEvent);
  });
  return summarizeMultiUploadState(itemStates, {
    batchId: batchState.batchId,
    context: batchState.context,
    status: batchState.status,
    startedAt: batchState.startedAt,
    completedAt: batchState.completedAt
  });
}

/**
 * Reduce a single item event. Every transition is guarded so only valid state changes take effect; illegal
 * transitions throw in development and are swallowed in production (last known good state stays).
 */
export function reduceMultiUploadItemState(
  uploadItemState: MediaManagerMultiUploadItemState,
  uploadItemEvent: MediaManagerMultiUploadItemEvent
): MediaManagerMultiUploadItemState {
  switch (uploadItemEvent.type) {
    case 'FileValidationPassed':
      if (uploadItemState.status !== 'pending-validation') {
        return handleInvalidMultiUploadItemTransition(uploadItemState, uploadItemEvent);
      }
      return {
        ...uploadItemState,
        status: 'validated',
        supportedMediaType: uploadItemEvent.supportedMediaType,
        withinSizeLimit: uploadItemEvent.withinSizeLimit,
        meetsRequirements: uploadItemEvent.meetsRequirements,
        errorMessage: null,
        resolutionMessage: null
      };

    case 'FileValidationFailed':
      if (uploadItemState.status !== 'pending-validation') {
        return handleInvalidMultiUploadItemTransition(uploadItemState, uploadItemEvent);
      }
      return {
        ...uploadItemState,
        status: 'invalid',
        supportedMediaType: uploadItemEvent.supportedMediaType,
        withinSizeLimit: uploadItemEvent.withinSizeLimit,
        meetsRequirements: uploadItemEvent.meetsRequirements,
        errorMessage: uploadItemEvent.errorMessage,
        resolutionMessage: null
      };

    case 'FileUploadStarted':
      if (uploadItemState.status !== 'prepared-for-upload') {
        return handleInvalidMultiUploadItemTransition(uploadItemState, uploadItemEvent);
      }
      return {
        ...uploadItemState,
        status: 'uploading',
        errorMessage: null,
        resolutionMessage: null
      };

    case 'FileUploadPrepared':
      if (uploadItemState.status !== 'validated') {
        return handleInvalidMultiUploadItemTransition(uploadItemState, uploadItemEvent);
      }
      return {
        ...uploadItemState,
        status: 'prepared-for-upload',
        fileName: uploadItemEvent.fileName,
        processingContext: uploadItemEvent.processingContext,
        errorMessage: null,
        resolutionMessage: null
      };

    case 'FileUploadCompleted':
      if (uploadItemState.status !== 'uploading') {
        return handleInvalidMultiUploadItemTransition(uploadItemState, uploadItemEvent);
      }
      return {
        ...uploadItemState,
        status: 'processing',
        transcodePercentComplete: uploadItemState.file?.isVideo?.()
          ? (uploadItemState.transcodePercentComplete ?? null)
          : null,
        errorMessage: null,
        resolutionMessage: null
      };

    case 'FileTranscodeProgressUpdated':
      if (!isVideoUploadAwaitingTranscodeProgress(uploadItemState)) {
        return uploadItemState;
      }
      return {
        ...uploadItemState,
        transcodePercentComplete: uploadItemEvent.percentComplete
      };

    case 'FileUploadFailed':
      if (
        uploadItemState.status !== 'validated' &&
        uploadItemState.status !== 'prepared-for-upload' &&
        uploadItemState.status !== 'uploading'
      ) {
        return handleInvalidMultiUploadItemTransition(uploadItemState, uploadItemEvent);
      }
      return {
        ...uploadItemState,
        status: 'failed',
        errorMessage: uploadItemEvent.errorMessage,
        resolutionMessage: null
      };

    case 'FileProcessingCompleted':
      if (uploadItemState.status !== 'processing') {
        return handleInvalidMultiUploadItemTransition(uploadItemState, uploadItemEvent);
      }
      return {
        ...uploadItemState,
        status: 'completed',
        uploadedAsset: uploadItemEvent.uploadedAsset,
        errorMessage: null,
        resolutionMessage: uploadItemEvent.resolutionMessage
      };

    case 'FileProcessingTimedOut':
      if (uploadItemState.status !== 'processing') {
        return handleInvalidMultiUploadItemTransition(uploadItemState, uploadItemEvent);
      }
      return {
        ...uploadItemState,
        status: 'processing-timed-out',
        errorMessage: uploadItemEvent.errorMessage,
        resolutionMessage: null
      };

    case 'FileProcessingFailed':
      if (uploadItemState.status !== 'processing') {
        return handleInvalidMultiUploadItemTransition(uploadItemState, uploadItemEvent);
      }
      return {
        ...uploadItemState,
        status: 'failed',
        errorMessage: uploadItemEvent.errorMessage,
        resolutionMessage: null
      };
    default: {
      const exhaustiveCheck: never = uploadItemEvent;
      return exhaustiveCheck;
    }
  }
}

/**
 * Reduce a batch-level event: batch start/refresh completion/failure, plus explicit clear-and-drop-failed hooks
 * used by the UI. Cross-batch events (stale batchId) are dropped so only the currently active batch reacts.
 */
export function reduceMultiUploadBatchState(
  batchState: MediaManagerMultiUploadState,
  batchEvent: MediaManagerMultiUploadBatchEvent
): MediaManagerMultiUploadState {
  if (batchEvent.type === 'BatchCleared') {
    return createEmptyMultiUploadState();
  }
  if (batchEvent.type === 'FailedUploadItemsRemoved') {
    const remainingItems = batchState.itemStates.filter(item => !UploadUtils.isUploadItemErrorStatus(item.status));
    if (remainingItems.length === 0) {
      return createEmptyMultiUploadState();
    }
    return summarizeMultiUploadState(remainingItems, {
      batchId: batchState.batchId,
      context: batchState.context,
      status: 'completed',
      startedAt: batchState.startedAt,
      completedAt: batchState.completedAt ?? new Date().toISOString()
    });
  }
  if (batchEvent.type !== 'BatchStarted' && batchEvent.batchId !== batchState.batchId) {
    return batchState;
  }
  switch (batchEvent.type) {
    case 'BatchStarted':
      return summarizeMultiUploadState(batchEvent.items, {
        batchId: batchEvent.batchId,
        context: batchEvent.context,
        status: 'running',
        startedAt: new Date().toISOString(),
        completedAt: null
      });

    case 'ViewsRefreshed':
      return {
        ...batchState,
        status: 'completed',
        completedAt: new Date().toISOString()
      };

    case 'BatchFailed':
      return {
        ...batchState,
        status: 'completed',
        completedAt: new Date().toISOString(),
        hasFailures: true
      };

    default: {
      const exhaustiveCheck: never = batchEvent;
      return exhaustiveCheck;
    }
  }
}

/**
 * A transition arrived for an item that does not accept it. Throws in development so the bug surfaces during
 * QA; falls back to the previous state in production so end users are never stuck.
 */
export function handleInvalidMultiUploadItemTransition(
  uploadItemState: MediaManagerMultiUploadItemState,
  uploadItemEvent: MediaManagerMultiUploadItemEvent
): MediaManagerMultiUploadItemState {
  if (!environment.production) {
    throw new Error(`Invalid multi-upload item transition: ${uploadItemState.status} -> ${uploadItemEvent.type}`);
  }
  return uploadItemState;
}

/**
 * Once every item has reached a terminal state (invalid/completed/timed-out/failed), the batch transitions
 * from 'running' to 'refreshing'; the domain model uses that as its cue to notify view listeners. Otherwise
 * the batch remains 'running'.
 */
export function deriveMultiUploadBatchStateFromItems(
  batchState: MediaManagerMultiUploadState
): MediaManagerMultiUploadState {
  if (batchState.status === 'idle' || batchState.status === 'completed') {
    return batchState;
  }
  const allItemsAreTerminal = batchState.itemStates.every(item => {
    return (
      item.status === 'invalid' ||
      item.status === 'completed' ||
      item.status === 'processing-timed-out' ||
      item.status === 'failed'
    );
  });
  return {
    ...batchState,
    status: allItemsAreTerminal ? 'refreshing' : 'running'
  };
}

/**
 * Fresh empty batch state used as the reducer's seed. Item counters are zeroed so downstream views can rely on
 * always-present number fields rather than doing existence checks everywhere.
 */
export function createEmptyMultiUploadState(): MediaManagerMultiUploadState {
  return {
    batchId: null,
    context: null,
    status: 'idle',
    startedAt: null,
    completedAt: null,
    hasFailures: false,
    itemStates: [],
    totalCount: 0,
    pendingValidationCount: 0,
    validatedCount: 0,
    preparedForUploadCount: 0,
    invalidCount: 0,
    uploadingCount: 0,
    processingCount: 0,
    completedCount: 0,
    processingTimedOutCount: 0,
    failedCount: 0
  };
}

/**
 * Roll a list of item states up into batch-level counters + a hasFailures rollup. Called by every reducer path
 * that mutates the item list so the counters stay authoritative.
 */
export function summarizeMultiUploadState(
  itemStates: MediaManagerMultiUploadItemState[],
  metadata?: {
    batchId?: string | null;
    context?: MediaManagerMultiUploadState['context'];
    status?: MediaManagerMultiUploadBatchStatus;
    startedAt?: string | null;
    completedAt?: string | null;
  }
): MediaManagerMultiUploadState {
  const counts = itemStates.reduce(
    (acc, item) => {
      switch (item.status) {
        case 'pending-validation':
          acc.pendingValidationCount++;
          break;
        case 'validated':
          acc.validatedCount++;
          break;
        case 'prepared-for-upload':
          acc.preparedForUploadCount++;
          break;
        case 'invalid':
          acc.invalidCount++;
          break;
        case 'uploading':
          acc.uploadingCount++;
          break;
        case 'processing':
          acc.processingCount++;
          break;
        case 'completed':
          acc.completedCount++;
          break;
        case 'processing-timed-out':
          acc.processingTimedOutCount++;
          break;
        case 'failed':
          acc.failedCount++;
          break;
      }
      return acc;
    },
    {
      pendingValidationCount: 0,
      validatedCount: 0,
      preparedForUploadCount: 0,
      invalidCount: 0,
      uploadingCount: 0,
      processingCount: 0,
      completedCount: 0,
      processingTimedOutCount: 0,
      failedCount: 0
    }
  );
  return {
    batchId: metadata?.batchId ?? null,
    context: metadata?.context ?? null,
    status: metadata?.status ?? 'idle',
    startedAt: metadata?.startedAt ?? null,
    completedAt: metadata?.completedAt ?? null,
    hasFailures: counts.failedCount > 0 || counts.processingTimedOutCount > 0 || counts.invalidCount > 0,
    itemStates,
    totalCount: itemStates.length,
    ...counts
  };
}

/** Wrap each incoming file in a pending-validation item state for the reducer's initial `BatchStarted` payload. */
export function createInitialMultiUploadItems(files: BudsenseFile[]): MediaManagerMultiUploadItemState[] {
  return (files ?? []).map(file => {
    return {
      file,
      fileName: file?.name ?? '',
      status: 'pending-validation',
      supportedMediaType: null,
      withinSizeLimit: null,
      meetsRequirements: null,
      errorMessage: null,
      uploadedAsset: null,
      resolutionMessage: null,
      transcodePercentComplete: null,
      processingContext: null
    } as MediaManagerMultiUploadItemState;
  });
}

/** Deterministic-enough batch id for correlating events + toasts across a single upload run. */
export function createMultiUploadBatchId(): string {
  return `multi-upload-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Only video uploads emit transcode-progress events; ignore progress updates for images/pdfs. */
export function isVideoUploadAwaitingTranscodeProgress(item: MediaManagerMultiUploadItemState): boolean {
  return (item.status === 'uploading' || item.status === 'processing') && (item.file?.isVideo?.() ?? false);
}

/** Size cap depends on media type: images share one limit, videos another. Pdfs currently fall under images. */
export function isWithinMultiUploadSizeLimit(file: BudsenseFile, mediaType: MediaType): boolean {
  const fileSize = file?.size ?? 0;
  if (MediaUtils.isVideo(mediaType)) {
    return fileSize <= UploadUtils.MAX_VIDEO_SIZE;
  }
  return fileSize <= UploadUtils.MAX_IMG_SIZE;
}

/** Build a validation event from an item state, deciding whether it passes both media-type and size checks. */
export function buildMultiUploadValidationEvent(
  item: MediaManagerMultiUploadItemState
): MediaManagerMultiUploadItemEvent {
  const mediaType = item.file?.getMediaType?.();
  const supportedMediaType = UploadUtils.ALLOWED_UPLOAD_MEDIA_TYPES.includes(mediaType);
  const withinSizeLimit = isWithinMultiUploadSizeLimit(item.file, mediaType);
  const meetsRequirements = supportedMediaType && withinSizeLimit;
  if (meetsRequirements) {
    return {
      type: 'FileValidationPassed',
      scope: 'item',
      file: item.file,
      supportedMediaType: true,
      withinSizeLimit: true,
      meetsRequirements: true
    };
  }
  return {
    type: 'FileValidationFailed',
    scope: 'item',
    file: item.file,
    supportedMediaType,
    withinSizeLimit,
    meetsRequirements: false,
    errorMessage: buildMultiUploadValidationErrorMessage(supportedMediaType, withinSizeLimit, mediaType)
  };
}

/** Human-readable error message that tells the user exactly which rules the rejected file broke. */
export function buildMultiUploadValidationErrorMessage(
  supportedMediaType: boolean,
  withinSizeLimit: boolean,
  mediaType: MediaType,
  config: AllowedUploadTypesConfig = {
    allowImage: true,
    allowVideo: true,
    allowPdf: false
  }
): string {
  const combinedFormatHint = UploadUtils.getCombinedFileFormatLabel(config);
  const mediaTypeGroup = MediaUtils.getMediaTypeGroup(mediaType);
  const sizeHint =
    mediaTypeGroup === MediaTypeGroup.Video ? UploadUtils.videoMaxSizeText : UploadUtils.imageMaxSizeText;
  if (!supportedMediaType && !withinSizeLimit) {
    return `File must be ${combinedFormatHint} and must be ${sizeHint} or smaller.`;
  }
  if (!supportedMediaType) {
    return `File must be ${combinedFormatHint}.`;
  }
  return `File must be ${sizeHint} or smaller.`;
}
